<script setup lang="ts">
import { computed } from 'vue';
import { useToast } from 'vue-toastification';
import { Loader2 } from 'lucide-vue-next';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select as UISelect, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Spinner from '@/components/ui/Spinner.vue';
import { bulkCountPayload, emptyEntry, evaluateBulkRow, isBulkCountEntryDirty, type BulkCountEntry, type BulkCountRowProblem } from '@/composables/inventory/bulkCount';
import { useBulkEntryDialog } from '@/composables/inventory/useBulkEntryDialog';
import { drugUnitValue, formatPercent, formatQuantity, PERCENT_TONE_CLASSES, percentTone, signedNumber } from '@/composables/inventory/inventoryTable';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineBulkCount, useSaveMedicineBulkCount } from '@/plugins/store/actions/queries/inventory';
import UnsavedInputWarning from './UnsavedInputWarning.vue';

const isOpen = defineModel<boolean>('open', { required: true });

const toast = useToast();
const t = (key: string, params: Record<string, unknown> = {}) => i18n.global.t(`views.health.inventory.bulkCount.${key}`, params);

// Every opening starts from a blank sheet; figures typed for a row survive a change of date, not of farm.
const {
    writableFarms,
    farmId,
    dateValue,
    dateOnly,
    todayIso,
    entries,
    serverErrors,
    generalErrors,
    data,
    rows,
    isFetching,
    isError,
    refetch,
    isSaving,
    submit,
    pendingAction,
    currentFarmName,
    requestFarm,
    requestOpen,
    confirm,
    keep,
} = useBulkEntryDialog({ isOpen, useRows: useMedicineBulkCount, useSave: useSaveMedicineBulkCount, entryFor: emptyEntry, isEntryDirty: isBulkCountEntryDirty });

const reasons = computed(() => data.value?.reasons ?? []);

const states = computed(() =>
    rows.value.flatMap((row) => {
        const entry = entries[drugUnitValue(row)];

        return entry ? [{ row, entry, state: evaluateBulkRow(row, entry) }] : [];
    })
);
const countedRows = computed(() => states.value.filter(({ state }) => !state.skipped));
const hasProblems = computed(() => countedRows.value.some(({ state }) => state.problems.length > 0));
const canSave = computed(() => farmId.value !== null && dateOnly.value !== '' && countedRows.value.length > 0 && !hasProblems.value && !isFetching.value && !isSaving.value);

function problemText(problem: BulkCountRowProblem): string {
    return t(`problems.${problem}`);
}

function reasonSelection(entry: BulkCountEntry): string | undefined {
    return entry.reasonId === null ? undefined : String(entry.reasonId);
}

function setReason(entry: BulkCountEntry, value: unknown) {
    entry.reasonId = typeof value === 'string' && value !== '' ? Number(value) : null;
}

async function save() {
    if (!canSave.value || farmId.value === null) {
        return;
    }

    const saved = await submit(bulkCountPayload(farmId.value, dateOnly.value, rows.value, entries));

    if (saved === null) {
        toast.error(t('saveFailed'));
    } else {
        toast.success(t('saved', { count: saved }));
    }
}
</script>

<template>
    <!-- Closing goes through the guard too (the X, Cancel, Esc or a click outside); a save closes directly. -->
    <Dialog :model-value="isOpen" :disabled="isSaving" class="sm:max-w-[1100px]" @update:model-value="requestOpen">
        <DialogHeader>
            <DialogTitle>{{ t('title') }}</DialogTitle>
            <DialogDescription class="mt-2 text-gray-500">{{ t('description') }}</DialogDescription>
        </DialogHeader>

        <!-- Farm and date share the forms' floating-label field style, so they line up with each other and with the purchase and waste forms. -->
        <div class="flex flex-wrap items-start gap-4">
            <div class="w-72">
                <!-- Picking another farm goes through the guard: with something typed, the farm stays until the switch is confirmed. -->
                <CustomSelect
                    :value="farmId"
                    :label="$t('views.health.inventory.table.headings.farm')"
                    :items="writableFarms"
                    item-text="name"
                    item-value="farm_id"
                    required
                    :allow-empty="false"
                    @update:value="requestFarm"
                />
            </div>
            <div class="w-48">
                <DatePicker v-model:value="dateValue" :resetable="false" required :max="todayIso" :label="$t('views.health.inventory.table.headings.date')" />
            </div>
        </div>

        <UnsavedInputWarning v-if="pendingAction?.kind === 'switchFarm'" action="switchFarm" :farm-name="currentFarmName" @confirm="confirm" @keep="keep" />

        <div v-if="isError" class="py-10 text-center text-gray-500 space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <div v-else-if="isFetching && rows.length === 0" class="py-10 flex justify-center">
            <Spinner class="size-6 animate-spin" />
        </div>
        <div v-else-if="rows.length === 0" class="py-10 text-center text-gray-500">{{ t('empty') }}</div>
        <div v-else class="max-h-[50vh] overflow-auto rounded-lg border border-gray-200">
            <table class="w-full text-normal">
                <thead class="sticky top-0 z-10 bg-gray-50 text-left text-gray-500">
                    <tr>
                        <th class="px-3 py-2 font-medium">{{ $t('views.health.inventory.table.headings.drug') }}</th>
                        <th class="px-3 py-2 text-right font-medium">{{ t('headings.calculated') }}</th>
                        <th class="px-3 py-2 font-medium w-40">{{ t('headings.counted') }}</th>
                        <th class="px-3 py-2 text-right font-medium">{{ t('headings.difference') }}</th>
                        <th class="px-3 py-2 text-right font-medium">{{ $t('views.health.inventory.table.headings.percent') }}</th>
                        <th class="px-3 py-2 font-medium w-56">{{ t('headings.reason') }}</th>
                        <th class="px-3 py-2 font-medium">{{ t('headings.note') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="{ row, entry, state } in states" :key="drugUnitValue(row)" class="border-t border-gray-200 align-top" :class="{ 'bg-gray-50 text-gray-400': state.skipped }">
                        <td class="px-3 py-2">
                            <span class="font-medium" :class="{ 'text-gray-900': !state.skipped }">{{ row.drug_name }}</span>
                            <span class="text-gray-500"> ({{ row.unit_name }})</span>
                        </td>
                        <td class="px-3 py-2 text-right whitespace-nowrap" :class="{ 'text-red-600': row.calculated < 0 }">{{ formatQuantity(row.calculated) }}</td>
                        <td class="px-3 py-2">
                            <div class="flex items-center gap-1.5">
                                <Input
                                    v-model="entry.counted"
                                    type="number"
                                    inputmode="decimal"
                                    min="0"
                                    step="any"
                                    autocomplete="off"
                                    class="h-9 bg-white"
                                    :aria-label="`${t('headings.counted')} ${row.drug_name}`"
                                />
                                <span class="text-sm text-gray-500">{{ row.unit_name }}</span>
                            </div>
                            <div v-if="state.problems.includes('invalidQuantity')" class="pt-1 text-sm text-red-600">{{ problemText('invalidQuantity') }}</div>
                        </td>
                        <td class="px-3 py-2 text-right whitespace-nowrap">
                            <span v-if="state.skipped" class="italic">{{ t('skipped') }}</span>
                            <span v-else-if="state.difference !== null" class="text-gray-900">{{ signedNumber(state.difference, formatQuantity) }}</span>
                        </td>
                        <td class="px-3 py-2 text-right">
                            <Badge v-if="state.percent !== null" variant="outline" class="whitespace-nowrap" :class="PERCENT_TONE_CLASSES[percentTone(state.percent)]">{{ formatPercent(state.percent) }}</Badge>
                            <span v-else class="text-gray-400" aria-hidden="true">-</span>
                        </td>
                        <td class="px-3 py-2">
                            <UISelect :model-value="reasonSelection(entry)" @update:model-value="setReason(entry, $event)">
                                <SelectTrigger
                                    class="rounded-lg bg-white border-gray-200 h-9 text-normal"
                                    :class="{ 'border-red-500': state.problems.includes('reasonRequired') }"
                                    :aria-label="`${t('headings.reason')} ${row.drug_name}`"
                                >
                                    <SelectValue :placeholder="t('reasonPlaceholder')" />
                                </SelectTrigger>
                                <SelectContent align="start" :body-lock="false" class="z-[2000]">
                                    <SelectGroup>
                                        <SelectItem v-for="reason in reasons" :key="reason.id" :value="String(reason.id)">{{ reason.name }}</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </UISelect>
                            <div v-if="state.problems.includes('reasonRequired')" class="pt-1 text-sm text-red-600">{{ problemText('reasonRequired') }}</div>
                        </td>
                        <td class="px-3 py-2">
                            <Input
                                v-model="entry.note"
                                type="text"
                                maxlength="500"
                                class="h-9 bg-white"
                                :class="{ 'border-red-500': state.problems.includes('noteRequired') }"
                                :aria-label="`${t('headings.note')} ${row.drug_name}`"
                            />
                            <div v-if="state.problems.includes('noteRequired')" class="pt-1 text-sm text-red-600">{{ problemText('noteRequired') }}</div>
                            <div v-if="serverErrors[drugUnitValue(row)]" class="pt-1 text-sm text-red-600">{{ serverErrors[drugUnitValue(row)] }}</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="generalErrors.length" class="text-sm text-red-600" role="alert">
            <div v-for="message in generalErrors" :key="message">{{ message }}</div>
        </div>

        <UnsavedInputWarning v-if="pendingAction?.kind === 'close'" action="close" :farm-name="currentFarmName" @confirm="confirm" @keep="keep" />

        <DialogFooter class="pt-2">
            <DialogClose as-child>
                <Button variant="outline" class="min-w-32" :disabled="isSaving">{{ t('cancel') }}</Button>
            </DialogClose>
            <!-- While saving the button stays at full colour with a spinner; save() itself ignores further clicks. -->
            <Button class="min-w-44" :disabled="!canSave && !isSaving" :aria-busy="isSaving" @click="save">
                <Loader2 v-if="isSaving" class="size-4 animate-spin" aria-hidden="true" />
                {{ t('save', { count: countedRows.length }) }}
            </Button>
        </DialogFooter>
    </Dialog>
</template>
